<template>
  <div class="bdb">
    <div v-if="loading" class="bdb-loading">
      <Spin fix>
        <Icon type="ios-loading" size="28" class="bdb-loading__spin" />
        <div class="bdb-loading__text">正在加载 {{ year }} 年数据…</div>
      </Spin>
    </div>

    <header class="bdb-hero">
      <div class="bdb-hero__kpis">
        <button
          v-for="kpi in heroKpis"
          :key="kpi.key"
          type="button"
          class="bdb-kpi"
          @click="goRoute(kpi.route)"
        >
          <span class="bdb-kpi__lbl">{{ kpi.label }}</span>
          <span class="bdb-kpi__val">{{ kpi.value }}<small v-if="kpi.unit">{{ kpi.unit }}</small></span>
        </button>
        <div class="bdb-hero__year">
          <select :value="year" :disabled="loading" @change="onYearChange">
            <option v-for="y in years" :key="y" :value="y">{{ y }}年</option>
          </select>
        </div>
      </div>
      <div class="bdb-hero__tags">
        <button
          v-for="tag in heroTags"
          :key="tag.key"
          type="button"
          class="bdb-tag"
          :class="{
            'bdb-tag--warn': tag.warn,
            'bdb-tag--ok': tag.ok,
          }"
          @click="tag.route && goRoute(tag.route)"
        >
          <span>{{ tag.label }}</span>
          <strong>{{ tag.value }}</strong>
        </button>
      </div>
    </header>

    <div v-if="error" class="bdb-error">
      <span>{{ error }}</span>
      <button type="button" @click="reload">重试</button>
    </div>

    <section class="bdb-stages">
      <article
        v-for="mod in moduleList"
        :key="mod.key"
        class="bdb-stage"
        :class="'bdb-stage--' + mod.key"
      >
        <header class="bdb-stage__head" @click="goRoute(mod.route)">
          <BaseIcon :icon="mod.icon" :size="15" />
          <h2>{{ mod.title }}</h2>
          <span class="bdb-stage__sum">
            {{ mod.summary.count || 0 }} 项 · {{ formatAmount(mod.summary.amount) }}
          </span>
          <BaseIcon icon="ios-arrow-forward" :size="12" class="bdb-stage__more" />
        </header>

        <div class="bdb-stage__tabs" @click.stop>
          <button
            type="button"
            class="bdb-gtab"
            :class="{'bdb-gtab--on': !moduleFilter[mod.key]}"
            @click="setModuleFilter(mod.key, '')"
          >
            全部 <em>{{ mod.summary.count || 0 }}</em>
          </button>
          <button
            v-for="s in (mod.statuses || [])"
            :key="s.name"
            type="button"
            class="bdb-gtab"
            :class="{'bdb-gtab--on': moduleFilter[mod.key] === s.name}"
            @click="setModuleFilter(mod.key, s.name)"
          >
            {{ s.name }} <em>{{ s.count }}</em>
          </button>
        </div>

        <ul class="bdb-stage__list" :key="mod.key + '-' + (moduleFilter[mod.key] || 'all')">
          <li
            v-for="(item, i) in (mod.items || []).slice(0, 6)"
            :key="item.id"
            @click.stop="openProject(mod.key, item)"
          >
            <em>{{ i + 1 }}</em>
            <span class="bdb-stage__name" :title="item.name">
              <i v-if="item.urgent" class="bdb-dot-urgent" />
              {{ item.name }}
            </span>
            <b :class="'bdb-st--' + statusClass(item.status)">{{ item.status }}</b>
            <span class="bdb-stage__amt">{{ formatAmount(item.amount) }}</span>
            <span class="bdb-stage__date">{{ item.date }}</span>
          </li>
          <li v-if="!(mod.items && mod.items.length)" class="bdb-stage__empty">
            {{ moduleFilter[mod.key] ? '该状态下暂无项目' : '暂无项目' }}
          </li>
        </ul>
      </article>
    </section>

    <section class="bdb-feeds">
      <article class="bdb-feed bdb-feed--danger">
        <header>
          <h3>截止预警</h3>
          <span>{{ deadlines.total || 0 }} 项临近</span>
        </header>
        <ul>
          <li
            v-for="item in deadlineList"
            :key="item.id || item._i"
            :class="{
              'is-empty': !item.id,
              'is-urgent': item.days_left <= 1,
              'is-warn': item.days_left > 1 && item.days_left <= 3,
            }"
            @click="item.id && openProject('work', item)"
          >
            <time>{{ item.id ? fmtMD(item.bid_deadline) : '' }}</time>
            <i />
            <div>
              <p>{{ item.name || '—' }}</p>
              <small v-if="item.id">{{ formatAmount(item.amount) }} · 剩 {{ item.days_left }} 天</small>
            </div>
          </li>
        </ul>
      </article>

      <article class="bdb-feed bdb-feed--amber">
        <header>
          <h3>保证金未退</h3>
          <span>{{ bonds.total || 0 }} 笔 · {{ formatAmount(bonds.total_amount) }}</span>
        </header>
        <ul>
          <li
            v-for="item in bondList"
            :key="item.id || item._i"
            :class="{'is-empty': !item.id, 'is-warn': item.overdue_days > 0}"
            @click="item.id && openProject('open', item)"
          >
            <time>{{ item.id ? bondTime(item) : '' }}</time>
            <i />
            <div>
              <p>{{ item.name || '—' }}</p>
              <small v-if="item.id">
                {{ formatAmount(item.deposit_amount) }}
                <template v-if="item.overdue_days > 0"> · 逾期 {{ item.overdue_days }} 天</template>
                <template v-else> · 应退 {{ fmtMD(item.deadline) }}</template>
              </small>
            </div>
          </li>
        </ul>
      </article>

      <article class="bdb-feed bdb-feed--blue">
        <header>
          <h3>等待开标</h3>
          <span>{{ waiting.total || 0 }} 项</span>
        </header>
        <ul>
          <li
            v-for="item in waitingList"
            :key="(item.module || 'open') + '-' + (item.id || item._i)"
            :class="{'is-empty': !item.id, 'is-soon': item.days_left != null && item.days_left <= 3}"
            @click="item.id && openProject(item.module || 'open', item)"
          >
            <time>{{ item.id ? fmtMD(item.open_date) : '' }}</time>
            <i />
            <div>
              <p>{{ item.name || '—' }}</p>
              <small v-if="item.id">{{ formatAmount(item.amount) }} · {{ waitingLabel(item) }}</small>
            </div>
          </li>
        </ul>
      </article>
    </section>

    <section class="bdb-analytics">
      <div class="bdb-charts">
        <div
          v-for="c in pieCharts"
          :key="c.key"
          class="bdb-chart"
        >
          <div :ref="'chart-' + c.key" class="bdb-chart__el" />
        </div>
        <div class="bdb-chart bdb-chart--trend">
          <div ref="chart-trend" class="bdb-chart__el" />
        </div>
      </div>

      <aside class="bdb-activity">
        <header>
          <h3>工作动态</h3>
        </header>
        <ul>
          <li
            v-for="(item, i) in activityList"
            :key="item.id || i"
            :class="{'is-empty': !item.id, 'is-clickable': !!(item.project_id && item.module)}"
            @click="item.project_id && item.module && openProject(item.module, {id: item.project_id})"
          >
            <time>{{ item.formatted_time || '' }}</time>
            <i :class="item.module ? 'is-' + item.module : ''" />
            <div>
              <template v-if="item.id">
                <strong>{{ item.user_name }}</strong>
                <span>{{ item.summary }}</span>
                <em>{{ item.project_name }}</em>
              </template>
              <template v-else>—</template>
            </div>
          </li>
        </ul>
      </aside>
    </section>

    <Viewer
      ref="detailViewer"
      v-model="showDetailViewer"
      drawer
      editable
      :data="detailData"
      :loading="detailLoading"
      :width="900"
      :model="detailModel"
      @edit="enterDetailEdit"
    />
    <Editor
      ref="detailEditor"
      v-model="showDetailEditor"
      drawer
      viewable
      :data="detailData"
      :loading="detailLoading"
      :width="900"
      :model="detailModel"
      @view="enterDetailView"
      @on-event="handleDetailEvent"
    />
  </div>
</template>

<script>
import moment from 'moment';
import * as echarts from 'echarts/core';
import {TooltipComponent, GridComponent, TitleComponent, LegendComponent} from 'echarts/components';
import {PieChart, LineChart, BarChart} from 'echarts/charts';
import {CanvasRenderer} from 'echarts/renderers';
import {LabelLayout, UniversalTransition} from 'echarts/features';
import {buildChartOption} from './bid-dashboard-chart-options';
import {
  API,
  unwrap,
  formatAmount,
  STATUS_STYLE,
  emptyOverview,
  emptyModule,
} from './bid-dashboard-api';
import Viewer from '@/components/model/view';
import Editor from '@/components/model/editor';

echarts.use([
  TooltipComponent,
  GridComponent,
  TitleComponent,
  LegendComponent,
  PieChart,
  LineChart,
  BarChart,
  CanvasRenderer,
  LabelLayout,
  UniversalTransition,
]);

const FEED_PAD = 6;
const ACT_PAD = 10;
const PIE_KEYS = [
  {key: 'scale', title: '规模'},
  {key: 'type', title: '类型'},
  {key: 'result', title: '结果'},
];

const DETAIL_META = {
  info: {
    model: 'BU_Bid_Project',
    getPath: (id) => `business/bid_projects/${id}?edit=1`,
    patchPath: (id) => `/business/bid_projects/${id}`,
  },
  work: {
    model: 'BU_Bid_Work_Project',
    getPath: (id) => `business/bid_work_projects/${id}?edit=1`,
    patchPath: (id) => `/business/bid_work_projects/${id}`,
  },
  open: {
    model: 'BU_Bid_Opening_Project',
    getPath: (id) => `business/bid_opening_projects/${id}?edit=1`,
    patchPath: (id) => `/business/bid_opening_projects/${id}`,
  },
};

function padList(list, n) {
  const arr = (list || []).slice(0, n);
  while (arr.length < n) arr.push({_i: `e-${arr.length}`});
  return arr;
}

export default {
  name: 'BidDashboardBoard',
  components: {Viewer, Editor},
  data() {
    return {
      year: new Date().getFullYear(),
      loading: false,
      error: '',
      overview: emptyOverview(),
      modules: {
        info: emptyModule('info'),
        work: emptyModule('work'),
        open: emptyModule('open'),
      },
      deadlines: {total: 0, list: []},
      bonds: {total: 0, total_amount: 0, list: []},
      waiting: {total: 0, list: []},
      activities: [],
      chartData: {
        scale: [],
        type: [],
        result: [],
        trend: [],
      },
      pieCharts: PIE_KEYS,
      moduleFilter: {
        info: '',
        work: '',
        open: '',
      },
      moduleLoading: {
        info: false,
        work: false,
        open: false,
      },
      ro: null,
      showDetailViewer: false,
      showDetailEditor: false,
      detailLoading: false,
      detailData: {},
      detailModule: '',
      detailModel: '',
    };
  },
  created() {
    this.chartInstances = {};
  },
  computed: {
    years() {
      const y = new Date().getFullYear();
      return [y, y - 1, y - 2];
    },
    heroKpis() {
      const o = this.overview;
      return [
        {key: 'amt', label: '控制价总额', value: formatAmount(o.tracking_amount), route: 'info'},
        {key: 'work', label: '在投项目', value: o.active_work || 0, unit: '项', route: 'work'},
        {key: 'open', label: '已开标', value: o.opened_count || 0, unit: '项', route: 'open'},
        {key: 'win', label: '中标', value: o.win_count || 0, unit: '项', route: 'open'},
        {key: 'rate', label: '中标率', value: o.win_rate || 0, unit: '%', route: 'open'},
        {key: 'winamt', label: '中标金额', value: formatAmount(o.win_amount), route: 'open'},
      ];
    },
    heroTags() {
      const o = this.overview;
      return [
        {key: 'info', label: '投标分析', value: `${o.info_count || 0} 项`, route: 'info'},
        {key: 'work', label: '投标工作', value: `${o.work_total || 0} 项`, route: 'work'},
        {key: 'wait', label: '等待开标', value: `${o.waiting_open || 0} 项`, route: 'open'},
        {key: 'urgent', label: '紧急在投', value: `${o.urgent_work || 0} 项`, warn: (o.urgent_work || 0) > 0, route: 'work'},
        {key: 'bond', label: '待退保证金', value: `${o.bond_pending || 0} 笔`, warn: (o.bond_pending || 0) > 0, route: 'open'},
        {key: 'audit', label: '待审核', value: `${o.pending_audit || 0} 项`, route: 'info'},
        {
          key: 'yoy',
          label: '同比增长',
          value: o.yoy_growth != null ? `${o.yoy_growth > 0 ? '+' : ''}${o.yoy_growth}%` : '—',
          ok: o.yoy_growth > 0,
        },
      ];
    },
    moduleList() {
      return ['info', 'work', 'open'].map((k) => {
        const mod = this.modules[k] || emptyModule(k);
        const filter = this.moduleFilter[k];
        const pool = (mod.allItems && mod.allItems.length) ? mod.allItems : (mod.items || []);
        let items;
        if (filter) {
          const fromApi = mod.items || [];
          const apiMatched = fromApi.length > 0 && fromApi.every((i) => i.status === filter);
          items = apiMatched
            ? fromApi.slice()
            : pool.filter((i) => i.status === filter);
        } else {
          items = pool.slice();
        }
        items = items.slice().sort((a, b) => (b.sort_at || 0) - (a.sort_at || 0));
        return {key: k, ...mod, items: items.slice(0, 6)};
      });
    },
    deadlineList() {
      const list = (this.deadlines.list || []).slice().sort((a, b) => (a.days_left || 0) - (b.days_left || 0));
      return padList(list, FEED_PAD);
    },
    bondList() {
      const list = (this.bonds.list || []).slice().sort((a, b) => {
        if ((a.overdue_days || 0) > 0 && (b.overdue_days || 0) > 0) {
          return (a.overdue_days || 0) - (b.overdue_days || 0);
        }
        if ((a.overdue_days || 0) > 0) return -1;
        if ((b.overdue_days || 0) > 0) return 1;
        return moment(a.deadline || 0).valueOf() - moment(b.deadline || 0).valueOf();
      });
      return padList(list, FEED_PAD);
    },
    waitingList() {
      const list = (this.waiting.list || []).slice().sort((a, b) => (a.days_left || 0) - (b.days_left || 0));
      return padList(list, FEED_PAD);
    },
    activityList() {
      return padList(this.activities, ACT_PAD);
    },
  },
  mounted() {
    this.$api.afterLogin(() => this.reload());
    this.bindResize();
  },
  activated() {
    this.$nextTick(() => this.resizeCharts());
  },
  beforeDestroy() {
    this.unbindResize();
    Object.keys(this.chartInstances || {}).forEach((k) => {
      if (this.chartInstances[k]) this.chartInstances[k].dispose();
    });
    this.chartInstances = {};
  },
  methods: {
    formatAmount,
    statusClass(s) {
      return STATUS_STYLE[s] || 'idle';
    },
    fmtMD(d) {
      return d ? moment(d).format('MM/DD') : '';
    },
    waitingLabel(item) {
      if (!item) return '待开标';
      if (item.days_left == null) return '开标日待定';
      if (item.days_left === 0) return '今天开标';
      return `${item.days_left} 天后开标`;
    },
    bondTime(item) {
      if (item.overdue_days > 0) return `逾${item.overdue_days}天`;
      return this.fmtMD(item.deadline);
    },
    goRoute(route) {
      if (!route) return;
      const base = this.$route.path.replace(/\/[^/]+$/, '');
      this.RouteTo(`${base}/${route}`);
    },
    openProject(moduleKey, item) {
      const meta = DETAIL_META[moduleKey];
      const id = item && (item.id || item.project_id);
      if (!meta || !id) return;
      this.detailModule = moduleKey;
      this.detailModel = meta.model;
      this.detailData = {};
      this.detailLoading = true;
      this.showDetailEditor = false;
      this.showDetailViewer = true;
      this.$api.get(meta.getPath(id)).then((res) => {
        this.detailData = (res && res.data && res.data.data) || {};
      }).catch((e) => {
        this.Error(e);
        this.showDetailViewer = false;
      }).finally(() => {
        this.detailLoading = false;
      });
    },
    enterDetailEdit() {
      if (!this.detailData || !this.detailData.id) return;
      this.showDetailViewer = false;
      this.showDetailEditor = true;
    },
    enterDetailView() {
      if (!this.detailData || !this.detailData.id) return;
      this.showDetailEditor = false;
      this.showDetailViewer = true;
    },
    handleDetailEvent({type, value}) {
      if (type === 'update') {
        this.updateDetailItem(value);
      } else if (type === 'cancel') {
        this.showDetailEditor = false;
      }
    },
    updateDetailItem(model) {
      const meta = DETAIL_META[this.detailModule];
      const id = this.detailData && this.detailData.id;
      if (!meta || !id) {
        if (this.$refs.detailEditor) this.$refs.detailEditor.stopLoading();
        return;
      }
      this.$api.patch(meta.patchPath(id), model).then(() => {
        this.$Notice.success({title: '修改成功'});
        this.showDetailEditor = false;
        this.reload();
      }).catch((e) => {
        this.Error(e);
      }).finally(() => {
        if (this.$refs.detailEditor) this.$refs.detailEditor.stopLoading();
      });
    },
    onYearChange(e) {
      const next = Number(e.target.value);
      if (!next || next === this.year) return;
      this.year = next;
      this.moduleFilter = {info: '', work: '', open: ''};
      this.reload();
    },
    setModuleFilter(key, status) {
      this.$set(this.moduleFilter, key, status || '');
      this.reloadModule(key);
    },
    async reloadModule(key) {
      if (!key) return;
      this.$set(this.moduleLoading, key, true);
      try {
        const status = this.moduleFilter[key];
        const query = {year: this.year, module: key, limit: 30};
        if (status) query.status = status;
        const data = await this.safeGet(API.modules, query);
        const next = data && data[key];
        if (!next) return;
        const prev = this.modules[key] || emptyModule(key);
        const cachedAll = prev.allItems || [];
        let allItems = cachedAll.slice();
        (next.items || []).forEach((row) => {
          if (!allItems.some((x) => x.id === row.id)) allItems.push(row);
        });
        if (!status && (next.items || []).length >= allItems.length) {
          allItems = (next.items || []).slice();
        }
        this.$set(this.modules, key, {
          ...prev,
          ...next,
          summary: prev.summary && prev.summary.count != null ? prev.summary : next.summary,
          statuses: (prev.statuses && prev.statuses.length) ? prev.statuses : (next.statuses || []),
          allItems,
          items: (next.items || []).slice(0, 6),
        });
      } finally {
        this.$set(this.moduleLoading, key, false);
      }
    },
    safeGet(url, query) {
      return this.$api.get(url, {params: query || {}}).then(unwrap).catch(() => null);
    },
    async reload() {
      if (this._reloading) return;
      this._reloading = true;
      this.loading = true;
      this.error = '';
      this.moduleFilter = {info: '', work: '', open: ''};
      try {
        const year = this.year;
        const [
          overview,
          modules,
          deadlines,
          bonds,
          waiting,
          activities,
          scale,
          type,
          result,
          trend,
        ] = await Promise.all([
          this.safeGet(API.overview, {year}),
          this.safeGet(API.modules, {year, limit: 50}),
          this.safeGet(API.deadlines, {year, limit: FEED_PAD}),
          this.safeGet(API.bonds, {year, limit: FEED_PAD}),
          this.safeGet(API.waiting, {year, limit: FEED_PAD}),
          this.safeGet(API.activities, {year, limit: ACT_PAD}),
          this.safeGet(API.charts, {type: 'scale', year}),
          this.safeGet(API.charts, {type: 'type', year}),
          this.safeGet(API.charts, {type: 'result', year}),
          this.safeGet(API.charts, {type: 'trend', year}),
        ]);

        if (!overview && !modules) {
          this.error = '看板数据加载失败，请确认后端接口已启动';
        }

        const wrapMod = (raw, key) => {
          const mod = raw || emptyModule(key);
          const allItems = (mod.items || []).slice();
          return {
            ...mod,
            allItems,
            items: allItems.slice(0, 6),
          };
        };

        this.overview = overview || emptyOverview();
        this.modules = {
          info: wrapMod(modules && modules.info, 'info'),
          work: wrapMod(modules && modules.work, 'work'),
          open: wrapMod(modules && modules.open, 'open'),
        };
        this.deadlines = deadlines || {total: 0, list: []};
        this.bonds = bonds || {total: 0, total_amount: 0, list: []};
        this.waiting = waiting || {total: 0, list: []};
        this.activities = activities || [];
        this.chartData = {
          scale: scale || [],
          type: type || [],
          result: result || [],
          trend: trend || [],
        };

        await this.$nextTick();
        this.renderAllCharts();
      } catch (e) {
        this.error = (e && e.message) || '加载失败';
      } finally {
        this.loading = false;
        this._reloading = false;
      }
    },
    getChartEl(key) {
      if (key === 'trend') return this.$refs['chart-trend'];
      const ref = this.$refs[`chart-${key}`];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    ensureChart(key) {
      const el = this.getChartEl(key);
      if (!el) return null;
      if (!this.chartInstances[key]) {
        this.chartInstances[key] = echarts.init(el, null, {renderer: 'canvas'});
      }
      return this.chartInstances[key];
    },
    renderAllCharts() {
      ['scale', 'type', 'result', 'trend'].forEach((key) => {
        const chart = this.ensureChart(key);
        if (!chart) return;
        chart.setOption(buildChartOption(key, this.chartData[key] || []), true);
        chart.resize();
      });
    },
    resizeCharts() {
      Object.keys(this.chartInstances || {}).forEach((k) => {
        if (this.chartInstances[k]) this.chartInstances[k].resize();
      });
    },
    bindResize() {
      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.resizeCharts);
        return;
      }
      this.ro = new ResizeObserver(() => this.resizeCharts());
      this.ro.observe(this.$el);
    },
    unbindResize() {
      if (this.ro) {
        this.ro.disconnect();
        this.ro = null;
      }
      window.removeEventListener('resize', this.resizeCharts);
    },
  },
};
</script>

<style lang="less" scoped>
/* 融入框架：直角、主题变量、轻边框 */
.bdb {
  position: relative;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  padding: 10px 12px 14px;
  background: var(--bg2, #f5f6f8);
  color: var(--text2, #333);
}

.bdb-loading {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.72);

  /deep/ .ivu-spin-fix {
    background: transparent;
  }
}

.bdb-loading__spin {
  animation: bdb-spin 0.9s linear infinite;
  color: var(--primary, #369);
}

.bdb-loading__text {
  margin-top: 10px;
  font-size: 13px;
  color: var(--subtext2, #888);
}

@keyframes bdb-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.bdb-hero {
  margin-bottom: 8px;
  background: var(--bg3, #fff);
  border: 1px solid var(--border3, #eee);
}

.bdb-hero__kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr)) auto;
  gap: 0;
  min-width: 0;
  border-bottom: 1px solid var(--border3, #eee);
}

.bdb-kpi {
  appearance: none;
  border: 0;
  border-right: 1px solid var(--border3, #eee);
  background: transparent;
  padding: 8px 6px;
  color: var(--text2, #333);
  cursor: pointer;
  text-align: center;
  transition: background 0.12s;

  &:hover { background: var(--bg2, #f9f9f9); }
}

.bdb-kpi__lbl {
  display: block;
  font-size: 11px;
  color: var(--subtext2, #888);
  margin-bottom: 2px;
}

.bdb-kpi__val {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: var(--primary, #369);
  font-variant-numeric: tabular-nums;
  line-height: 1.15;

  small {
    font-size: 11px;
    margin-left: 2px;
    font-weight: 600;
    color: var(--subtext2, #888);
  }
}

.bdb-hero__year {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 10px;
  background: var(--bg2, #f9f9f9);

  select {
    appearance: none;
    border: 1px solid var(--border3, #eee);
    background: var(--bg3, #fff);
    color: var(--text2, #333);
    height: 26px;
    padding: 0 22px 0 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    outline: none;

    &:disabled { opacity: 0.6; cursor: wait; }
  }

  &::after {
    content: '';
    position: absolute;
    right: 18px;
    top: 50%;
    margin-top: -2px;
    border: 4px solid transparent;
    border-top-color: var(--subtext2, #888);
    pointer-events: none;
  }
}

.bdb-hero__tags {
  display: flex;
  gap: 0;
  overflow: hidden;
  background: var(--bg2, #f9f9f9);
}

.bdb-tag {
  flex: 1;
  min-width: 0;
  border: 0;
  border-right: 1px solid var(--border3, #eee);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
  cursor: pointer;
  color: var(--subtext2, #888);

  &:last-child { border-right: 0; }

  span { font-size: 11px; }
  strong {
    font-size: 12px;
    color: var(--text2, #333);
    font-weight: 700;
  }

  &--warn strong { color: var(--error, #cc4040); }
  &--ok strong { color: var(--success, #028a09); }

  &:hover {
    background: var(--bg3, #fff);
    strong { color: var(--primary, #369); }
  }
}

.bdb-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  padding: 8px 10px;
  background: rgba(204, 64, 64, 0.08);
  border: 1px solid rgba(204, 64, 64, 0.25);
  color: var(--error, #cc4040);
  font-size: 13px;

  button {
    border: 0;
    background: var(--error, #cc4040);
    color: #fff;
    padding: 4px 10px;
    cursor: pointer;
  }
}

.bdb-stages {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;
}

.bdb-stage {
  background: var(--bg3, #fff);
  border: 1px solid var(--border3, #eee);
  display: flex;
  flex-direction: column;
  min-height: 260px;

  &--info { border-top: 2px solid var(--primary, #369); }
  &--work { border-top: 2px solid var(--bg1, #465369); }
  &--open { border-top: 2px solid var(--warning, #d4941c); }
}

.bdb-stage__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 4px;
  cursor: pointer;
  color: var(--primary, #369);
  background: var(--bg2, #f9f9f9);
  border-bottom: 1px solid var(--border3, #eee);

  h2 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--text2, #333);
  }
}

.bdb-stage__sum {
  margin-left: auto;
  font-size: 11px;
  color: var(--subtext2, #888);
}

.bdb-stage__more { color: var(--subtext3, #aaa); }

.bdb-stage__tabs {
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border3, #eee);
  overflow-x: auto;
  background: var(--bg3, #fff);

  &::-webkit-scrollbar {
    height: 0;
  }
}

.bdb-gtab {
  appearance: none;
  flex: 0 0 auto;
  border: 1px solid var(--border3, #eee);
  background: transparent;
  color: var(--subtext2, #888);
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 22px;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.12s, border-color 0.12s, background 0.12s;

  em {
    font-style: normal;
    margin-left: 3px;
    font-weight: 700;
    color: var(--text2, #333);
  }

  &:hover {
    border-color: var(--primary, #369);
    color: var(--primary, #369);
  }

  &--on {
    border-color: var(--primary, #369);
    color: var(--primary, #369);
    background: rgba(54, 102, 153, 0.08);

    em { color: var(--primary, #369); }
  }
}

.bdb-stage__list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border3, #eee);
  flex: 1;

  li {
    display: grid;
    grid-template-columns: 18px 1fr auto auto auto;
    gap: 6px;
    align-items: center;
    padding: 0 8px;
    height: 32px;
    border-bottom: 1px solid var(--border3, #eee);
    cursor: pointer;
    font-size: 12px;

    &:hover { background: var(--bg2, #f9f9f9); }
    &:last-child { border-bottom: 0; }
  }

  em {
    font-style: normal;
    color: var(--subtext3, #aaa);
    font-size: 11px;
    text-align: center;
  }
}

.bdb-stage__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.bdb-dot-urgent {
  display: inline-block;
  width: 5px;
  height: 5px;
  background: var(--warning, #d4941c);
  margin-right: 4px;
  vertical-align: middle;
}

.bdb-st--done,
.bdb-st--active,
.bdb-st--idle {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 4px;
  white-space: nowrap;
}

.bdb-st--done {
  background: rgba(54, 102, 153, 0.12);
  color: var(--primary, #369);
}
.bdb-st--active {
  background: var(--primary, #369);
  color: #fff;
}
.bdb-st--idle {
  background: rgba(212, 148, 28, 0.15);
  color: var(--warning, #d4941c);
}

.bdb-stage__amt {
  width: 52px;
  text-align: right;
  color: var(--primary, #369);
  font-weight: 700;
  font-size: 11px;
}

.bdb-stage__date {
  width: 36px;
  text-align: right;
  color: var(--subtext3, #aaa);
  font-size: 11px;
}

.bdb-stage__empty {
  justify-content: center !important;
  color: var(--subtext3, #aaa);
  cursor: default !important;
  grid-template-columns: 1fr !important;
}

.bdb-feeds {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;
}

.bdb-feed {
  background: var(--bg3, #fff);
  border: 1px solid var(--border3, #eee);
  min-height: 210px;
  display: flex;
  flex-direction: column;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 10px;
    border-bottom: 1px solid var(--border3, #eee);
    background: var(--bg2, #f9f9f9);

    h3 {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
    }
    span {
      font-size: 11px;
      font-weight: 600;
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 2px 0;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  li {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    min-height: 0;
    cursor: pointer;
    border-bottom: 1px solid var(--border3, #eee);

    &:last-child { border-bottom: 0; }
    &:hover:not(.is-empty) { background: var(--bg2, #f9f9f9); }

    time {
      width: 42px;
      flex-shrink: 0;
      text-align: right;
      font-size: 10px;
      font-weight: 700;
    }

    > i {
      width: 6px;
      height: 6px;
      flex-shrink: 0;
      background: var(--subtext3, #aaa);
    }

    div {
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    p {
      margin: 0;
      font-size: 12px;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }

    small {
      flex-shrink: 0;
      font-size: 10px;
      color: var(--subtext2, #888);
    }

    &.is-empty {
      opacity: 0.28;
      cursor: default;
    }
  }

  &--danger {
    header { color: var(--error, #cc4040); }
    li time { color: var(--error, #cc4040); }
    li > i { background: var(--error, #cc4040); }
    li.is-urgent p { color: var(--error, #cc4040); font-weight: 800; }
    li.is-warn p { color: var(--warning, #d4941c); }
  }

  &--amber {
    header { color: var(--warning, #d4941c); }
    li time { color: var(--warning, #d4941c); }
    li > i { background: var(--warning, #d4941c); }
    li.is-warn p { color: var(--warning, #d4941c); }
  }

  &--blue {
    header { color: var(--primary, #369); }
    li time { color: var(--primary, #369); }
    li > i { background: var(--primary, #369); }
    li.is-soon p { color: var(--text2, #333); }
  }
}

.bdb-analytics {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(260px, 0.9fr);
  gap: 8px;
  min-height: 300px;
}

.bdb-charts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: 170px 190px;
  gap: 8px;
}

.bdb-chart {
  background: var(--bg3, #fff);
  border: 1px solid var(--border3, #eee);
  overflow: hidden;
  min-height: 0;

  &--trend { grid-column: 1 / -1; }
}

.bdb-chart__el {
  width: 100%;
  height: 100%;
}

.bdb-activity {
  background: var(--bg3, #fff);
  border: 1px solid var(--border3, #eee);
  display: flex;
  flex-direction: column;

  header {
    padding: 8px 10px;
    border-bottom: 1px solid var(--border3, #eee);
    background: var(--bg2, #f9f9f9);

    h3 {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 2px 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  li {
    flex: 1;
    min-height: 28px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 10px;
    border-bottom: 1px solid var(--border3, #eee);
    font-size: 12px;

    &:last-child { border-bottom: 0; }
    &.is-empty { opacity: 0.28; }
    &.is-clickable {
      cursor: pointer;
      &:hover { background: var(--bg2, #f9f9f9); }
    }

    time {
      width: 72px;
      flex-shrink: 0;
      text-align: right;
      font-size: 10px;
      color: var(--subtext2, #888);
    }

    > i {
      width: 6px;
      height: 6px;
      background: var(--primary, #369);
      flex-shrink: 0;

      &.is-work { background: var(--bg1, #465369); }
      &.is-open { background: var(--warning, #d4941c); }
      &.is-info { background: var(--primary, #369); }
    }

    div {
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: center;
      gap: 6px;
      overflow: hidden;
    }

    strong {
      flex-shrink: 0;
      color: var(--primary, #369);
      font-weight: 700;
    }

    span {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    em {
      flex-shrink: 0;
      max-width: 38%;
      font-style: normal;
      font-size: 10px;
      color: var(--subtext3, #aaa);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

@media (max-width: 1200px) {
  .bdb-hero__kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .bdb-hero__year { grid-column: 1 / -1; justify-content: flex-end; padding: 6px 10px; }
  .bdb-stages,
  .bdb-feeds { grid-template-columns: 1fr; }
  .bdb-analytics { grid-template-columns: 1fr; }
  .bdb-charts { grid-template-rows: 150px 150px 180px; }
}

@media (max-width: 768px) {
  .bdb { padding: 8px; }
  .bdb-hero__kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .bdb-hero__tags { flex-wrap: wrap; }
  .bdb-tag { flex: 1 1 45%; border-bottom: 1px solid var(--border3, #eee); }
  .bdb-charts { grid-template-columns: 1fr; grid-template-rows: repeat(4, 170px); }
  .bdb-chart--trend { grid-column: auto; }
}
</style>
